import { Avatar, Flex, Group, Text } from "@mantine/core";
import {
  getRowClasses,
  minimizedSize,
} from "../../../lists/ListingWithContent/listing";
import NoItemsModal from "../../../../../components/TableComponents/NoItemsModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteNurturing,
  editNurturing,
  getNurturings,
} from "../../../../../api/PRMAPI";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { IconChevronDown } from "@tabler/icons-react";
import ItemDeleter from "../../../../../components/TableComponents/ItemDeleter";

const NurturingsList = ({
  minState,
  setMinState,
  nurturing,
  setNurturing,
  loading,
  setLoading,
}) => {
  const lang = useLanguage((s) => s.language);
  const [rows, setRows] = useState([]);
  const apiRef = useGridApiRef();

  const columns = useMemo(() => {
    if (nurturing) {
      return [
        nameColumn({
          disableLink: true,
          width: 290,
          editFunction: (row) => editNurturing,
          handleClick: () => {},
          editorStyle: { right: 16 },
          getDisabled: () => false,
          apiRef,
          leftSection: function (row) {
            return (
              <Avatar
                color="initials"
                name={row.name}
                radius={"md"}
                size={36}
                variant="filled"
              />
            );
          },
        }),
        dateColumn(),
      ];
    } else
      return [
        numberColumn(),
        nameColumn({
          handleClick: () => {},
          disableLink: true,
          editFunction: (row) => editNurturing,
          getDisabled: () => false,
          apiRef,
          width: 350,
        }),
        dateColumn(),
      ];
  }, [nurturing, apiRef]);

  const handleRowClick = useCallback(({ row }) => {
    setMinState("minimized");
    setNurturing(row);
  }, []);

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4} pos={"relative"}>
          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteNurturing}
            deletedMessage={lang.prm.nurturing.delete_success}
            id={row.id}
            onFinish={() => setLoading(true)}
          />

          <IconChevronDown
            size={24}
            className="bump"
            opacity={nurturing?.id === row.id ? 1 : 0.4}
            style={{
              transform: nurturing?.id === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [handleRowClick, nurturing]
  );

  useEffect(() => {
    if (nurturing) {
      apiRef.current.setColumnVisibility("created_on", false);
    } else {
      apiRef.current.setColumnVisibility("created_on", true);
    }
  }, [apiRef, nurturing]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getNurturings,
    responseField: "nurturings",
    setRows,
  });

  return (
    <Flex
      flex={1}
      maw={minState === "minimized" ? 0 : nurturing ? minimizedSize - 81 : null}
      pos={"relative"}
      style={{
        display: minState === "minimized" ? "none" : "flex",
      }}
    >
      <NoItemsModal
        rows={rows}
        loading={loading}
        text={
          <Text size="xl" mb={32}>
            {lang.prm.nurturing.no_nurturings}
          </Text>
        }
      />
      <MainTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={loading}
        setLoading={setLoading}
        id={"model-table"}
        actionsCell={actionsCell}
        onRowClick={handleRowClick}
        whiteButtons={!nurturing}
        actionsCellWidth={100}
        disableGotoPage={nurturing}
        disableVisibleColumns={nurturing}
        defaultPerPage={15}
        disablePerPage={nurturing}
        disableCheckboxes={!!nurturing}
        getRowClassName={(params) =>
          getRowClasses({
            params,
            mainId: nurturing?.id,
            integerId: nurturing?.id,
            isHoverSource: true,
          })
        }
        withSorter={!!nurturing}
        tableSx={{
          "& .MuiDataGrid-columnHeaders": {
            border: "none !important",
            display: nurturing ? "none" : "flex",
          },
          "& .MuiDataGrid-cell": {
            border: "none",
          },
          "& .MuiDataGrid-main": {
            border: "none !important",
            borderRadius: 3,
          },
          "& .table-search": {
            width: nurturing ? "200px !important" : "300px !important",
          },
          "& .MuiDataGrid-footerContainer": {
            border: "none !important",
          },
          "& .table-row": {
            cursor: "pointer",
            borderRadius: 3,
            marginBottom: 1,
            background: "#fff",
            border: "1px solid var(--mantine-color-primary-1)",
          },
          "& .MuiDataGrid-pinnedColumnHeaders": {
            background: "transparent",
            borderRight: "none !important",
          },
          "& .MuiDataGrid-pinnedColumns--right": {
            border: "none !important",
          },
          "& .MuiDataGrid-pinnedColumns--left": {
            border: "none !important",
          },
          "& .MuiDataGrid-pinnedColumns--right .table-row": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            background: "#fff",
            border: "1px solid   var(--mantine-color-primary-1)",
            borderLeft: "none !important",
          },
          "& .MuiDataGrid-pinnedColumns--left .table-row": {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            background: "#fff",
            border: "1px solid   var(--mantine-color-primary-1)",
            borderRight: "none !important",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
            borderRadius: nurturing ? 3 : 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
          "& .MuiDataGrid-selectedRowCount": {
            visibility: nurturing ? "hidden" : "visible",
          },
          "& .MuiPagination-root": {
            left: nurturing ? 0 : undefined,
            transform: nurturing ? "none" : undefined,
          },
        }}
      />
    </Flex>
  );
};

export default NurturingsList;
